<template>
  <div data-component="app-launcher-update">
    <span class="title">
      {{ title }}
    </span>

    <span class="description">
      {{ $t('HITL.app-launcher.update-progress-description') }}
    </span>

    <div class="progress">
      <div class="progress-bar-container">
        <div
          class="progress-bar"
          :style="mProgressStyle"
        />
      </div>

      <!-- eslint-disable vue/no-v-html -->
      <span
        class="progress-percent"
        v-html="mProgressLabel"
      />

      <span class="progress-items">
        {{ activeMigrationId }}
      </span>
    </div>

    <div
      v-if="showItemsProgress"
      class="progress"
    >
      <div class="progress-bar-container">
        <div
          class="progress-bar"
          :style="iProgressStyle"
        />
      </div>

      <span
        class="progress-percent"
        v-html="iProgressLabel"
      />
    </div>
  </div>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent, PropType } from 'vue-demi';

/** Progress is measured as a number of migrations or records */
export default defineComponent({
  name: 'AppLauncherUpdate',
  props: {
    version: {
      type: String,
      default: '',
    },
    pending: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    complete: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    reports: {
      type: Object as PropType<Record<string, { items: number; total: number; }>>,
      default: () => {},
    },
  },
  computed: {
    title() {
      return this.$t('HITL.app-launcher.update-progress-title', {
        version: this.version,
      });
    },
    mProgress() {
      const progress = this.pending.length
        ? this.complete.length / (this.complete.length + this.pending.length)
        : 1;
      return Math.round(progress * 100);
    },
    mProgressStyle() {
      return { width: `${this.mProgress}%` };
    },
    mProgressLabel() {
      const migrations = `${this.complete.length} / ${this.complete.length + this.pending.length}`;
      return this.$t('HITL.app-launcher.update-progress-migrations', { migrations });
    },
    activeMigrationId() {
      return this.pending[0];
    },
    activeMigrationReport() {
      return this.reports[this.activeMigrationId] || {};
    },
    showItemsProgress() {
      return !_.isEmpty(this.activeMigrationReport);
    },
    iProgressStyle() {
      const r = this.activeMigrationReport;
      const progress = r.total
        ? r.items / r.total
        : r.items / Number.MAX_SAFE_INTEGER;
      return { width: `${Math.round(progress * 100)}%` };
    },
    iProgressLabel() {
      const r = this.activeMigrationReport || {};
      const items = r.total
        ? `${r.items}/${r.total}`
        : r.items;
      return this.$t('HITL.app-launcher.update-progress-records', { items });
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;

[data-component='app-launcher-update'] {
  display: flex;
  flex-direction: column;
  width: 100%;
  line-height: 1.5;

  @include md {
    width: 40rem;
  }

  .title,
  .description {
    color: var(--or-c-on-background);
  }

  .title {
    font-size: 1.25rem;
    font-weight: var(--fw-2);
  }

  .description {
    margin-top: var(--s-3);
  }

  .progress {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto 1fr;
    gap: var(--s-3);
    width: 100%;
    margin-top: var(--s-5);

    &-bar-container {
      grid-area: 1 / 1 / span 1 / span 2;
      height: 0.375rem;
      width: 100%;
      background: var(--or-c-primary-container);
      border-radius: var(--s-2);
    }

    &-bar {
      height: 100%;
      background: var(--or-c-on-primary-container);
      border-radius: var(--s-2);
    }

    &-percent,
    &-items {
      color: var(--or-c-on-surface);
    }

    &-percent {
      font-size: 1rem;
      strong {
        font-weight: var(--fw-2);
      }
    }

    &-items {
      grid-area: 2 / 2 / span 1 / span 1;
      text-align: right;
    }
  }
}
</style>
